import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	Grid,
	IconButton,
	Skeleton,
	Tooltip,
	Typography,
} from '@mui/material';
import { FC } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import {
	selectInvoiceProformaLoading,
	selectOmnivaLabelLoading,
	selectOrderDetails,
	selectOrderDetailsLoading,
} from 'redux/reducers/euDdp/selectors';
import { getInvoiceProformaRequest, getOmnivaLabelRequest } from 'redux/reducers/euDdp/reducer';
import { getSavedUrl } from 'redux/reducers/auth/selectors';

// Statuses for which the Omniva label can be downloaded:
// 4 - cleared (Released by Customs), 8 - omniva_printed (Delivery to end customer accepted)
import { OrderError } from 'components/OrderManagement/Order/OrderError';
import { EMManagementIdHeader } from 'components/EMManagement/EMManagementId/EMManagementIdHeader';
import { PATHS } from 'routes/constants';
import { OrderDetails } from './OrderDetails';
import { ShipmentDetails } from './ShipmentDetails';
import { ShippingDetails } from './ShippingDetails';
import { DeliveryInformation } from './DeliveryInformation';
import { ShipmentTimeLine } from './ShipmentTimeLine';
import { AmountBreakdown } from './AmountBreakdown';
import {
	DELIVERY_STATUS_FILTER,
	EEuDdpDeliveryStatusId,
	EuDdpOrderStatusId,
	ORDER_STATUS_COLORS,
	ORDER_STATUS_FILTER,
} from '../constants';
import { COLORS } from '../../../services/constants/colors';
import { ETableStructureType } from '../../../ui/EasTable/types';
import { EasTableValue } from '../../../ui/EasTable/EasTableRow/EasTableValue';
import { tableStructure as euDdpTableStructure } from '../tableStructure';
import { tableStructure as orderManagementTableStructure } from '../../OrderManagement/tableStructure';
import { getTrackingPageUrl } from '../utils';
import { CloudDownload, LocalShipping } from '@mui/icons-material';

export const EuDdpOrder: FC = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const order = useSelector(selectOrderDetails);
	const orderIsLoading = useSelector(selectOrderDetailsLoading);
	const omnivaLabelLoading = useSelector(selectOmnivaLabelLoading);
	const invoiceProformaLoading = useSelector(selectInvoiceProformaLoading);
	const savedUrl = useSelector(getSavedUrl);

	const handleReplayHistory = () => {
		if (savedUrl && savedUrl.includes(PATHS.EU_DDP_ORDERS_PAGE)) {
			navigate(savedUrl);
		} else {
			navigate(PATHS.EU_DDP_ORDERS_PAGE);
		}
	};

	const handleGetOmnivaLabel = () => {
		if (!order?.order.id) return;
		dispatch(
			getOmnivaLabelRequest({
				orderId: order.order.id,
				fileName: `omniva_label_${order.order.external_order_id}.pdf`,
			}),
		);
	};

	const handleGetInvoiceProforma = () => {
		if (!order?.order.id) return;
		dispatch(
			getInvoiceProformaRequest({
				orderId: order.order.id,
				fileName: `proforma_invoice_${order.order.external_order_id}.pdf`,
			}),
		);
	};

	const canGetOmnivaLabel =
		!!order?.order &&
		(order.order.shipping_status_id === EuDdpOrderStatusId.CLEARED ||
			order.order.shipping_status_id === EuDdpOrderStatusId.DELIVERY_TO_END_CUSTOMER_ACCEPTED);

	if (!orderIsLoading && !order?.order) {
		return (
			<OrderError
				text="Order not found"
				linkText="Go to the EU DDP Orders list"
				link={PATHS.EU_DDP_ORDERS_PAGE}
			/>
		);
	}

	const handleTrackShipment = () => {
		if (!order?.order.tracking_number) return;
		window.open(getTrackingPageUrl(order.order.tracking_number), '_blank');
	};

	return (
		<Box>
			<Box sx={{ padding: '0px 20px 0 20px', maxWidth: '100vw', overflowX: 'auto' }}>
				<Box sx={{ minWidth: '800px' }}>
					{!orderIsLoading && !!order && (
						<>
							<EMManagementIdHeader
								savedUrl={savedUrl}
								emIdProp={order.order.em_id}
								showBackButton={false}
							/>
							<Divider sx={{ my: 1, borderColor: COLORS.PRIMARY }} />
							<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
								<Box sx={{ mb: 2 }}>
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'start',
											alignItems: 'center',
											gap: 1,
										}}
									>
										<Tooltip title="Back to the EU DDP Orders list">
											<IconButton onClick={handleReplayHistory} color="primary">
												<KeyboardBackspaceIcon />
											</IconButton>
										</Tooltip>

										<Typography variant="h5" color="primary" component="h2" fontWeight="bold">
											EU DDP Order:
										</Typography>

										<Typography variant="h5" color="primary">
											{order.order.external_order_id}
										</Typography>
										{order.order.status && (
											// <Chip
											// 	label={order.order.status.replace('_', ' ')}
											// 	size="small"
											// 	sx={{
											// 		p: '0 5px',
											// 		ml: 1,
											// 		fontSize: '.7rem',
											// 		height: '20px',
											// 		backgroundColor: 'primary.light',
											// 		color: '#000000a0',
											// 	}}
											// />
											<EasTableValue
												value={order.order.status}
												type={ETableStructureType.STATUS}
												size="m"
												tooltip="Order Status"
												chip
												tableStructureItem={orderManagementTableStructure.find(
													(item) => item.selector === 'order_status',
												)}
											/>
										)}
										{order.order.shipping_status && (
											// <Chip
											// 	label={order.order.shipping_status}
											// 	size="small"
											// 	sx={{
											// 		p: '0 5px',
											// 		fontSize: '.8rem',
											// 		height: '20px',
											// 		bgcolor:
											// 			ORDER_STATUS_COLORS.find(
											// 				(status) => status[0] === order.order.shipping_status,
											// 			)?.[1] || 'primary.light',
											// 		color: '#000000a0',
											// 	}}
											// />
											<EasTableValue
												value={order.order.shipping_status}
												type={ETableStructureType.STATUS}
												size="m"
												tooltip="Shipping Status"
												chip
												tableStructureItem={euDdpTableStructure.find(
													(item) => item.selector === 'orderStatus',
												)}
											/>
										)}
									</Box>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'row',
											justifyContent: 'start',
											gap: '20px',
										}}
									>
										{order.order.sale_date_provided_by_em && (
											<Typography variant="body2" gutterBottom color="GrayText">
												Payment date:{' '}
												{DateTime.fromISO(order.order.sale_date_provided_by_em).toFormat(
													'yyyy-MM-dd, HH:mm',
												)}
											</Typography>
										)}
										{order.order.reporting_payment_date && (
											<Typography variant="body2" gutterBottom color="GrayText">
												Reporting date:{' '}
												{DateTime.fromISO(order.order.reporting_payment_date).toFormat(
													'yyyy-MM-dd, HH:mm',
												)}
											</Typography>
										)}
									</Box>
								</Box>
								<Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
									<Button
										variant="contained"
										color="primary"
										size="small"
										onClick={handleGetInvoiceProforma}
										disabled={invoiceProformaLoading}
										startIcon={
											invoiceProformaLoading ? (
												<CircularProgress size={16} color="inherit" />
											) : (
												<CloudDownload />
											)
										}
									>
										Proforma invoice
									</Button>
									{canGetOmnivaLabel && (
										<Button
											variant="contained"
											color="primary"
											size="small"
											onClick={handleGetOmnivaLabel}
											disabled={omnivaLabelLoading}
											startIcon={
												omnivaLabelLoading ? (
													<CircularProgress size={16} color="inherit" />
												) : (
													<CloudDownload />
												)
											}
										>
											Get Omniva label
										</Button>
									)}
									{/* <Button
										variant="contained"
										color="primary"
										size="small"
										startIcon={<LocalShipping />}
										onClick={handleTrackShipment}
									>
										Track shipment
									</Button> */}
								</Box>
							</Box>
						</>
					)}
					{orderIsLoading && (
						<Box sx={{ mb: 2 }}>
							<Skeleton variant="rectangular" height="80px" sx={{ borderRadius: '12px' }} />
						</Box>
					)}
					<Grid container spacing={2}>
						<Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
							<OrderDetails />
							<ShipmentDetails />
						</Grid>

						<Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
							<ShippingDetails />
							<DeliveryInformation />
							<ShipmentTimeLine />
							<AmountBreakdown />
						</Grid>
					</Grid>
				</Box>
			</Box>
		</Box>
	);
};
